import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { clearUserAttributesCache } from "../../../app/auth/authSessionAttributes";
import { useAuth } from "../../../app/providers/AuthProvider";
import {
  confirmLoginChallenge,
  resolveLoginUsername,
  signInWithPassword,
  startPasswordReset,
} from "../api/login";
import { METHOD_LOGIN_KEY, authRoutes } from "../config/authRoutes";
import {
  getLoginPasswordHelperText,
  getLoginUsernameHelperText,
  isInvalidCredentialsMessage,
  normalizeLoginError,
  normalizeLoginStateMessage,
} from "../utils/authErrorMessages";

const loginSteps = {
  credentials: "credentials",
  mfa: "mfa",
};

export function useLoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, refreshUser } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [message, setMessage] = useState(
    normalizeLoginStateMessage(location.state?.message),
  );
  const [step, setStep] = useState(loginSteps.credentials);
  const [challengeUser, setChallengeUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setCheckingSession(false);
      return undefined;
    }

    navigate(authRoutes.dashboard, { replace: true });
    return undefined;
  }, [isAuthenticated, navigate]);

  const isMfa = step === loginSteps.mfa;
  const messageSeverity = /sucesso/i.test(message) ? "success" : "error";
  const hasFormError = Boolean(message) && messageSeverity === "error";

  const usernameError = hasFormError && (
    !username || isInvalidCredentialsMessage(message)
  );
  const passwordError = hasFormError && (
    !password || isInvalidCredentialsMessage(message)
  );
  const mfaError = hasFormError && isMfa;

  const usernameHelperText = usernameError
    ? getLoginUsernameHelperText(message)
    : undefined;
  const passwordHelperText = passwordError
    ? getLoginPasswordHelperText(message)
    : undefined;
  const mfaHelperText = mfaError ? message : undefined;

  async function completeSignIn() {
    clearUserAttributesCache();
    await refreshUser(false);
    navigate(authRoutes.dashboard, { replace: true });
  }

  async function submitCredentials() {
    if (!username || !password) {
      setMessage("Por favor, preencha todos os campos.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const cognitoUsername = await resolveLoginUsername(username);
      const response = await signInWithPassword({
        username: cognitoUsername,
        password,
      });

      sessionStorage.setItem(
        METHOD_LOGIN_KEY,
        username.includes("@") ? "EMAIL" : "USERNAME",
      );

      if (response.isSignedIn) {
        await completeSignIn();
        return;
      }

      if (response.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_EMAIL_CODE") {
        setChallengeUser(response);
        setStep(loginSteps.mfa);
        setMessage("");
        return;
      }

      if (response.nextStep?.signInStep === "RESET_PASSWORD") {
        await startPasswordReset({ username: cognitoUsername });
        navigate(`${authRoutes.resetPassword}?username=${cognitoUsername}`);
        return;
      }

      sessionStorage.setItem(METHOD_LOGIN_KEY, "");
      setMessage(normalizeLoginError(response.message));
    } catch (error) {
      sessionStorage.setItem(METHOD_LOGIN_KEY, "");
      setMessage(normalizeLoginError(error.message));
    } finally {
      setLoading(false);
    }
  }

  async function submitMfa() {
    if (!mfaCode) {
      setMessage("Informe o código enviado por e-mail.");
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      await confirmLoginChallenge({
        challengeResponse: mfaCode,
        user: challengeUser,
      });
      await completeSignIn();
    } catch (error) {
      setMessage(normalizeLoginError(error.message));
      setMfaCode("");
    } finally {
      setLoading(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (isMfa) {
      await submitMfa();
      return;
    }

    await submitCredentials();
  }

  return {
    checkingSession,
    handleSubmit,
    isMfa,
    loading,
    message,
    messageSeverity,
    mfaCode,
    mfaError,
    mfaHelperText,
    password,
    passwordError,
    passwordHelperText,
    setMfaCode,
    setPassword,
    setUsername,
    username,
    usernameError,
    usernameHelperText,
  };
}
